import { useQueryClient } from "@tanstack/react-query";

import { useT } from "@/3-features/i18n/useT";
import { PATHS } from "@/3-features/navigation/routes";
import { Image } from "@/4-components/primitives/Image";
import { Link } from "@/5-shared/routing";
import type { BlogPostSummary } from "@/5-shared/types";
import { iso2EUDate } from "@/5-shared/utils/format";
import { blogPostQuery } from "@/6-api/queries";

/** Newest-first, both the API listing and the landing band order posts this way. */
export const sortByRecency = <T extends { created_at: string }>(posts: T[]): T[] =>
  [...posts].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));

/**
 * One blog card, image band with category chip, title, author + date footer.
 * Links to the post by default; `noLink` renders a plain `<article>` for the
 * landing band's editorial feed, which has no live articles to open. `priority`
 * loads the image at once (the listing's first card is its LCP); `level` is the
 * title's heading level, 2 where the cards sit straight under the page's `<h1>`.
 */
export function BlogCard({
  post,
  noLink = false,
  priority = false,
  level = 3,
}: {
  post: BlogPostSummary;
  noLink?: boolean;
  priority?: boolean;
  level?: 2 | 3;
}) {
  const { t } = useT();
  const Title = level === 2 ? "h2" : "h3";
  const queryClient = useQueryClient();
  const cls = "group card card-hover flex h-full flex-col overflow-hidden";

  // Warm the post body before the click so opening it is instant (RQ dedupes,
  // and the 5-min staleTime means the click reuses this fetch).
  const prefetch = () => void queryClient.prefetchQuery(blogPostQuery(post.id));

  const inner = (
    <>
      <div className="relative w-full shrink-0">
        <Image src={post.image} alt={post.title ?? ""} priority={priority} className="aspect-4/3 w-full object-cover" draggable={false} />
        <span className="absolute left-3 bottom-3 rounded-full bg-dec-green px-3 py-1 text-xs font-semibold text-white">{post.category}</span>
      </div>
      <div className="flex flex-1 flex-col justify-between px-5 py-4">
        <Title className="card-title card-title--compact line-clamp-3 leading-snug transition-colors group-hover:text-dec-green">{post.title}</Title>
        <div className="mt-4 flex items-end justify-between footnote lg:text-sm">
          <span className="min-w-0 truncate pr-2">{t("byAuthor", { author: post.author })}</span>
          <time className="shrink-0 whitespace-nowrap" dateTime={post.created_at}>
            {iso2EUDate(post.created_at)}
          </time>
        </div>
      </div>
    </>
  );

  return noLink ? (
    <article className={cls}>{inner}</article>
  ) : (
    <Link
      to={`${PATHS.blog}?id=${post.id}`}
      className={cls}
      onMouseEnter={prefetch}
      onFocus={prefetch}
    >
      {inner}
    </Link>
  );
}
